import { useCallback, useRef } from "react";
import { useDeepCompareEffect } from "./global";
import useAsyncFn from "./useAsyncFn";

interface IDefaultOptions<T, S> {
  onSuccess?: (data: T) => S;
  triggerOnMount?: boolean;
}

const defaultOptions = {
  onSuccess: (data: any) => data,
  triggerOnMount: true,
};

/**
 * @deprecated in favor of react-query
 *
 * This custom hook is useful for Fetch api calls that we want to trigger
 * automatically on component mount and each time the params change
 *
 * This custom hook is useful when you do NOT need the redux state
 * to handle the api response because the result is a local state for example.
 * This way you can avoid the effort of creating actions, reducers, etc.
 *
 *
 * @param {fn} fn: api function to call (Must return a Promise)
 * @param {array} params: array containing the fn parameters. Any change to those
 *                        params will trigger an api call to fn
 * @param {object} opts: { onSuccess: called to process the api call result data,
 *                            triggerOnMount: skip the automatic fetch onMount (will still refetch automatically on params change)
 *                          }
 * @return {object} containing the api status props (data, loading, error, loaded)
 *                  AND a function to retry
 */
export function useAsync<T = any, S = T>(
  fn: (...args: any) => unknown,
  params: unknown[] = [],
  opts: IDefaultOptions<T, S> = {}
): {
  loading: boolean;
  data: S;
  error: any;
  loaded: boolean;
  retry: () => void;
} {
  const options = { ...defaultOptions, ...opts };
  const [state, callback] = useAsyncFn(async () => fn(...params), params, {
    onSuccess: options.onSuccess,
  });
  const didMountRef = useRef(options.triggerOnMount);

  useDeepCompareEffect(() => {
    const asyncCall = async () => {
      await callback();
    };
    if (didMountRef.current) {
      asyncCall();
    } else {
      didMountRef.current = true;
    }
  }, [params]);

  const retry = useCallback(() => {
    if (state.loading) {
      // TODO add new FE logger package and log info
      // if (process.env.NODE_ENV === 'development') {
      //   logger.info('You are calling useAsync hook retry() method while loading in progress, this is a no-op.')
      // }

      return;
    }
    return callback();
  }, [callback, state.loading]);

  return {
    ...state,
    retry,
  };
}
